import React, { useEffect, useMemo, useState } from 'react'
import API from '../../../../services/API'
import {
  Alert,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import {
  Inventory2,
  LockOpen,
  MedicalServices,
  Storefront,
} from '@mui/icons-material'
import DrugsBagCard from './DrugBagCard'

const SummaryTile = ({ icon: Icon, label, value, tone }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.25,
      borderRadius: 2,
      border: '1px solid rgba(15,23,42,0.08)',
      backgroundColor: '#fff',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          color: tone,
          backgroundColor: `${tone}14`,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Stack minWidth={0}>
        <Typography fontSize="1.25rem" lineHeight={1} fontWeight={950}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Stack>
    </Stack>
  </Paper>
)

const DrugBagSelector = () => {
  const [loading, setLoading] = useState(true)
  const [bags, setBags] = useState([])
  const [groupedBags, setGroupedBags] = useState({})
  const [fetchError, setFetchError] = useState(false)

  useEffect(() => {
    API.get('/medsmx/bags?populateassignments=true')
      .then((res) => {
        if (res.status === 200) {
          const grouped = {}

          res.data.forEach((bag) => {
            const groupKey = bag.assignedToStore
              ? bag.assignedToStore.name
              : 'Out of store'

            if (!grouped[groupKey]) grouped[groupKey] = []
            grouped[groupKey].push(bag)
          })

          setGroupedBags(grouped)
          setBags(res.data)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const groups = useMemo(
    () =>
      Object.entries(groupedBags).sort(([a], [b]) => {
        if (a === 'Out of store') return -1
        if (b === 'Out of store') return 1
        return a.localeCompare(b)
      }),
    [groupedBags],
  )

  const outOfStoreCount = groupedBags['Out of store']?.length || 0
  const removedSealCount = bags.filter(
    (bag) => bag.tagNumber === 'Removed',
  ).length

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ pb: 3, maxWidth: 1680, mx: 'auto', width: '100%' }}
    >
      <Stack spacing={0.5}>
        <Typography variant="h4" fontWeight={950}>
          Check Medicine Bag
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a bag to view contents, assign it, return it, or record checks.
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1,
        }}
      >
        <SummaryTile
          icon={MedicalServices}
          label="Medicine bags"
          value={bags.length}
          tone="#1E7F88"
        />
        <SummaryTile
          icon={Storefront}
          label="Out of store"
          value={outOfStoreCount}
          tone={outOfStoreCount > 0 ? '#D97706' : '#16A34A'}
        />
        <SummaryTile
          icon={LockOpen}
          label="Seal removed"
          value={removedSealCount}
          tone={removedSealCount > 0 ? '#DC2626' : '#16A34A'}
        />
        <SummaryTile
          icon={Inventory2}
          label="Storage groups"
          value={groups.length}
          tone="#2563EB"
        />
      </Box>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error">Unable to fetch bags. Try again later.</Alert>
      )}

      {!loading && groups.length === 0 && !fetchError ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px dashed rgba(15,23,42,0.18)',
            backgroundColor: '#F8FAFC',
          }}
        >
          <Typography fontWeight={900}>No medicine bags found.</Typography>
          <Typography variant="body2" color="text.secondary">
            Bags will appear here once they have been created.
          </Typography>
        </Paper>
      ) : (
        groups.map(([groupName, groupBags]) => (
          <Paper
            elevation={0}
            key={groupName}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: '1px solid rgba(15,23,42,0.08)',
              backgroundColor: '#fff',
            }}
          >
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Typography fontWeight={950}>{groupName}</Typography>
                <Chip
                  size="small"
                  label={groupBags.length}
                  sx={{ borderRadius: 1, fontWeight: 850 }}
                />
              </Stack>
              <Grid container spacing={1}>
                {groupBags.map((bag) => (
                  <DrugsBagCard bag={bag} key={bag._id} />
                ))}
              </Grid>
            </Stack>
          </Paper>
        ))
      )}
    </Box>
  )
}

export default DrugBagSelector
